import styled from 'styled-components'
import { breakpoint } from '@sketch/global-styles'

// Icons
import { ReactComponent as PencilIcon } from '@sketch/icons/pencil-16'

// Shared Styles
import {
  SubtitleTextWrapper,
  TableWrapper,
  Image,
} from 'modules/shares/components/DocumentItem/DocumentItem.styles'
import { OnDraggedOverTooltip } from '../CollectionShareDrop/CollectionShareDrop.styles'

export const StyledSubtitleTextWrapper = styled(SubtitleTextWrapper)`
  display: block;

  b {
    display: block;
    margin-bottom: 4px;
  }
`

export const Thumbnail = styled(Image)`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  overflow: hidden;

  /* Create the border inside */
  :after {
    border-radius: 4px;

    content: '';
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.border.A};
    position: absolute;

    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
`

export const ThumbnailWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  column-gap: 8px;
  row-gap: 8px;
  padding: 8px;

  height: 100%;
  width: 100%;
  top: 0;
  right: 0;
  position: absolute;

  ${breakpoint('xs')`
    column-gap: 16px;
    row-gap: 16px;
    padding: 16px;
  `}
`

export const NoThumbnailWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.border.A};
`

export const StyledStatusIcon = styled.div`
  height: 64px;
  width: 64px;
  /* This color should be "Light/colors.foreground.secondary.4" but given the icon composition */
  /* Opacity needs to be applied to the whole object */
  color: ${({ theme }) => theme.colors.foreground.secondary.A};
  opacity: 0.55;
`

export const InputWrapper = styled.div`
  position: relative;
`

export const StyledInput = styled.input`
  min-width: 100px;
  height: 100%;

  margin: -6px -6px -6px -2px; /* stylelint-disable-line scales/space */
  padding: 2px; /* stylelint-disable-line scales/space */
  font-weight: 500;
  font-size: ${({ theme }) => theme.fontSizes.E};

  background: none;
  outline: none;
  overflow: hidden;

  border: none;
  box-shadow: none;
  border-radius: ${({ theme }) => theme.radii.large};

  :focus {
    margin-left: -6px; /* stylelint-disable-line scales/space */
    padding-left: 4px;
    border: 2px solid ${({ theme }) => theme.colors.sketch.E};
  }

  ::selection {
    background: ${({ theme }) => theme.colors.sketch.E};
  }

  ::placeholder {
    color: ${({ theme }) => theme.colors.state.disabled};
  }
`

export const Pencil = styled(PencilIcon)`
  margin-left: 8px;
  width: 16px;

  /* This color should be "Light/colors.foreground.secondary.4" but given the icon composition */
  /* Opacity needs to be applied to the whole object */
  color: ${({ theme }) => theme.colors.foreground.secondary.A};
  opacity: 0.55;
`

export const DroppableTableWrapper = styled(TableWrapper)<{
  $isDraggedOver: boolean
}>`
  /*
   * We can't add an inset box-shadow to TableWrapper since it is a <tr> and so
   * is not "display: block".
   * Instead we use a bit of a hack and add a border to the <td>s within to
   * simulate a border around the entire <tr>
   * We add a border to the left, top and bottom of the first <td>, then to the
   * top and bottom of the middle <td>s and finally to the top, right and
   * bottom of the last <td>:
   *
   *    -----------------------------------
   *   | first-child | middle | last-child |
   *    -----------------------------------
   */
  td {
    /* Add a transparent border so that the elements don't change size */
    border: 4px solid transparent;

    /* Show the border on the top and bottom of all <td>s in the row */
    border-style: solid none solid none;

    /* Set the border to orange when we are dragging a share over the collection */
    ${({ $isDraggedOver, theme }) =>
      $isDraggedOver && `border-color: ${theme.colors.sketch.D};`}
  }

  td:first-child {
    /* Include a border on the left, top and bottom of the first <td> in the
     * row.
     */
    border-style: solid none solid solid;
    /* Reduce the left padding to compensate for the space taken up by the
     * border
     */
    padding-left: 8px;
  }

  td:last-child {
    /* Include a border on the top, right and bottom of the last <td> in the
     * row
     */
    border-style: solid solid solid none;
  }
`

export const DroppableTableWrapperDragTooltip = styled(OnDraggedOverTooltip)`
  top: auto;
  margin-top: -52px;
`
